import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Download, RefreshCw, WifiOff, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../auth/AuthContext';
import { requestServiceWorkerUpdate } from '../../pwa/serviceWorkerRegistration';
import { getWebPushPublicKey, saveWebPushSubscription } from '../../services/api';
import { isIosDevice, isMobileViewport, isStandalonePwa, urlBase64ToUint8Array } from '../../utils/pwa';
import './PWAStatus.scss';

const INSTALL_DISMISSED_KEY = 'foth_pwa_install_dismissed';
const INSTALL_PROMPTED_KEY = 'foth_pwa_install_prompted';
const INSTALL_PENDING_KEY = 'foth_pwa_install_pending';
const NOTIFICATION_PROMPTED_KEY = 'foth_push_prompted';
const NOTIFICATION_ENABLED_KEY = 'foth_push_enabled';
const NOTIFICATION_DISMISSED_KEY = 'foth_push_dismissed';
const NOTIFICATION_SNOOZE_UNTIL_KEY = 'foth_push_snooze_until';
const NOTIFICATION_UNAVAILABLE_UNTIL_KEY = 'foth_push_unavailable_until';
const PUSH_PROMPT_SNOOZE_MS = 2 * 24 * 60 * 60 * 1000;
const PUSH_UNAVAILABLE_RETRY_MS = 24 * 60 * 60 * 1000;
const CREATOR_ENGAGEMENT_PATHS = [
  '/creator/feed',
  '/creator/applications',
  '/creator/invited',
  '/creator/offers',
  '/creator/collabs',
];

const isCreatorEngagementPath = (pathname) => (
  CREATOR_ENGAGEMENT_PATHS.some((path) => pathname.startsWith(path))
);

const getStoredTimestamp = (key) => Number(localStorage.getItem(key) || 0);

const setFutureTimestamp = (key, milliseconds) => {
  localStorage.setItem(key, String(Date.now() + milliseconds));
};

const PWAStatus = () => {
  const { t } = useTranslation('common');
  const { currentUser, isAuthenticated } = useAuth();
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installOpportunity, setInstallOpportunity] = useState(() => localStorage.getItem(INSTALL_PENDING_KEY) === 'true');
  const installToastId = useRef(null);
  const notificationToastId = useRef(null);
  const hasShownUpdatePrompt = useRef(false);
  const isCreator = isAuthenticated && currentUser?.user_type === 'creator';

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      localStorage.removeItem(INSTALL_PENDING_KEY);
      setDeferredPrompt(null);
      setInstallOpportunity(false);
      toast.success(t('pwa.installed'));
    };

    const handleInstallOpportunity = () => {
      localStorage.setItem(INSTALL_PENDING_KEY, 'true');
      setInstallOpportunity(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('foth:pwa-install-opportunity', handleInstallOpportunity);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('foth:pwa-install-opportunity', handleInstallOpportunity);
    };
  }, [t]);

  useEffect(() => {
    if (
      !installOpportunity
      || !isCreator
      || !isMobileViewport()
      || isStandalonePwa()
      || !isCreatorEngagementPath(location.pathname)
    ) {
      return;
    }

    if (localStorage.getItem(INSTALL_DISMISSED_KEY) === 'true') {
      return;
    }

    if (localStorage.getItem(INSTALL_PROMPTED_KEY) === 'true') {
      return;
    }

    if (installToastId.current && toast.isActive(installToastId.current)) {
      return;
    }

    if (isIosDevice()) {
      localStorage.setItem(INSTALL_PROMPTED_KEY, 'true');
      installToastId.current = toast.info(
        ({ closeToast }) => (
          <div className="pwa-toast">
            <span>{t('pwa.install_ios')}</span>
            <button
              type="button"
              onClick={() => {
                localStorage.setItem(INSTALL_DISMISSED_KEY, 'true');
                localStorage.removeItem(INSTALL_PENDING_KEY);
                setInstallOpportunity(false);
                closeToast();
              }}
            >
              <X size={16} aria-hidden="true" />
              {t('pwa.not_now')}
            </button>
          </div>
        ),
        { autoClose: false }
      );
      return;
    }

    if (!deferredPrompt) return;

    localStorage.setItem(INSTALL_PROMPTED_KEY, 'true');
    installToastId.current = toast.info(
      ({ closeToast }) => (
        <div className="pwa-toast">
          <span>{t('pwa.install_available')}</span>
          <div className="pwa-toast__actions">
            <button
              type="button"
              onClick={async () => {
                closeToast();
                deferredPrompt.prompt();
                await deferredPrompt.userChoice;
                localStorage.removeItem(INSTALL_PENDING_KEY);
                setInstallOpportunity(false);
                setDeferredPrompt(null);
              }}
            >
              <Download size={16} aria-hidden="true" />
              {t('pwa.install')}
            </button>
            <button
              type="button"
              className="is-secondary"
              onClick={() => {
                localStorage.setItem(INSTALL_DISMISSED_KEY, 'true');
                localStorage.removeItem(INSTALL_PENDING_KEY);
                setInstallOpportunity(false);
                closeToast();
              }}
            >
              {t('pwa.not_now')}
            </button>
          </div>
        </div>
      ),
      { autoClose: 12000 }
    );
  }, [deferredPrompt, installOpportunity, isCreator, location.pathname, t]);

  useEffect(() => {
    const isSnoozed = getStoredTimestamp(NOTIFICATION_SNOOZE_UNTIL_KEY) > Date.now();
    const isUnavailableCoolingDown = getStoredTimestamp(NOTIFICATION_UNAVAILABLE_UNTIL_KEY) > Date.now();
    const canPrompt = isCreator
      && isStandalonePwa()
      && 'Notification' in window
      && 'PushManager' in window
      && 'serviceWorker' in navigator
      && Notification.permission === 'default'
      && localStorage.getItem(NOTIFICATION_ENABLED_KEY) !== 'true'
      && localStorage.getItem(NOTIFICATION_DISMISSED_KEY) !== 'true'
      && !isSnoozed
      && !isUnavailableCoolingDown
      && isCreatorEngagementPath(location.pathname);

    if (!canPrompt) return;
    if (notificationToastId.current && toast.isActive(notificationToastId.current)) return;

    let isCancelled = false;

    const snoozeNotifications = (closeToast) => {
      setFutureTimestamp(NOTIFICATION_SNOOZE_UNTIL_KEY, PUSH_PROMPT_SNOOZE_MS);
      localStorage.setItem(NOTIFICATION_PROMPTED_KEY, 'true');
      closeToast();
    };

    const dismissNotifications = (closeToast) => {
      localStorage.setItem(NOTIFICATION_DISMISSED_KEY, 'true');
      localStorage.setItem(NOTIFICATION_PROMPTED_KEY, 'true');
      closeToast();
    };

    const showNotificationPrompt = async () => {
      let publicKey = '';

      try {
        const keyResponse = await getWebPushPublicKey();
        publicKey = keyResponse.data?.public_key || '';
      } catch (error) {
        publicKey = '';
      }

      if (isCancelled) return;

      if (!publicKey) {
        setFutureTimestamp(NOTIFICATION_UNAVAILABLE_UNTIL_KEY, PUSH_UNAVAILABLE_RETRY_MS);
        return;
      }

      notificationToastId.current = toast.info(
        ({ closeToast }) => (
          <div className="pwa-toast">
            <span>{t('pwa.notifications_benefit')}</span>
            <div className="pwa-toast__actions">
              <button
                type="button"
                onClick={async () => {
                  localStorage.setItem(NOTIFICATION_PROMPTED_KEY, 'true');

                  try {
                    const permission = await Notification.requestPermission();
                    if (permission !== 'granted') {
                      dismissNotifications(closeToast);
                      return;
                    }

                    const registration = await navigator.serviceWorker.ready;
                    const existingSubscription = await registration.pushManager.getSubscription();
                    const subscription = existingSubscription || (await registration.pushManager.subscribe({
                      userVisibleOnly: true,
                      applicationServerKey: urlBase64ToUint8Array(publicKey),
                    }));

                    await saveWebPushSubscription(subscription.toJSON());
                    localStorage.setItem(NOTIFICATION_ENABLED_KEY, 'true');
                    localStorage.removeItem(NOTIFICATION_DISMISSED_KEY);
                    localStorage.removeItem(NOTIFICATION_SNOOZE_UNTIL_KEY);
                    localStorage.removeItem(NOTIFICATION_UNAVAILABLE_UNTIL_KEY);
                    closeToast();
                    toast.success(t('pwa.notifications_enabled'));
                  } catch (error) {
                    setFutureTimestamp(NOTIFICATION_UNAVAILABLE_UNTIL_KEY, PUSH_UNAVAILABLE_RETRY_MS);
                    closeToast();
                    toast.info(t('pwa.notifications_unavailable'));
                  }
                }}
              >
                <Download size={16} aria-hidden="true" />
                {t('pwa.enable_notifications')}
              </button>
              <button
                type="button"
                className="is-secondary"
                onClick={() => snoozeNotifications(closeToast)}
              >
                {t('pwa.remind_later')}
              </button>
              <button
                type="button"
                className="is-ghost"
                onClick={() => dismissNotifications(closeToast)}
              >
                {t('pwa.no_thanks')}
              </button>
            </div>
          </div>
        ),
        {
          autoClose: false,
          closeOnClick: false,
          onClose: () => {
            if (
              localStorage.getItem(NOTIFICATION_ENABLED_KEY) !== 'true'
              && localStorage.getItem(NOTIFICATION_DISMISSED_KEY) !== 'true'
              && getStoredTimestamp(NOTIFICATION_SNOOZE_UNTIL_KEY) <= Date.now()
              && Notification.permission === 'default'
            ) {
              setFutureTimestamp(NOTIFICATION_SNOOZE_UNTIL_KEY, PUSH_PROMPT_SNOOZE_MS);
            }
          },
        }
      );
    };

    showNotificationPrompt();

    return () => {
      isCancelled = true;
    };
  }, [isCreator, location.pathname, t]);

  useEffect(() => {
    const handleUpdateReady = (event) => {
      if (hasShownUpdatePrompt.current) return;
      hasShownUpdatePrompt.current = true;

      const registration = event.detail?.registration;

      toast.info(
        ({ closeToast }) => (
          <div className="pwa-toast">
            <span>{t('pwa.update_available')}</span>
            <button
              type="button"
              onClick={() => {
                closeToast();
                let refreshing = false;
                navigator.serviceWorker.addEventListener('controllerchange', () => {
                  if (refreshing) return;
                  refreshing = true;
                  window.location.reload();
                });
                requestServiceWorkerUpdate(registration);
              }}
            >
              <RefreshCw size={16} aria-hidden="true" />
              {t('pwa.refresh')}
            </button>
          </div>
        ),
        { autoClose: false }
      );
    };

    window.addEventListener('foth:pwa-update-ready', handleUpdateReady);
    return () => window.removeEventListener('foth:pwa-update-ready', handleUpdateReady);
  }, [t]);

  if (isOnline) {
    return null;
  }

  return (
    <aside className="pwa-offline-banner" role="status" aria-live="polite">
      <WifiOff size={17} aria-hidden="true" />
      <span>{t('pwa.offline')}</span>
      <button type="button" aria-label={t('pwa.dismiss')} onClick={() => setIsOnline(true)}>
        <X size={16} aria-hidden="true" />
      </button>
    </aside>
  );
};

export default PWAStatus;
